<script setup lang="ts">
import { computed, reactive } from "vue";
import useVuelidate from "@vuelidate/core";

import { Address, DefenceQuestion, PensionFund, useApplicationStore } from "../stores/application";
import { useQuoteStore } from "@/stores/quotes";
import AddressSuggestion from "./AddressSuggestion.vue";
import { PensionType } from "../entities/pensionFunds";
import { TaxFreeType } from "@/stores/pensionOptions";
import { Input, Question, TextArea, RadioSet, Radio, Select } from "./fields";
import { pensionFundsValidator } from "../utils/validators";
import { isDecimal, getTaxFreeCash } from "../utils";
import DefenceLineQuestion from "./DefenceLineQuestion.vue";
import { scrollErrorIntoView } from "../composables/scrollErrorIntoView";
import { tooltips } from "@/tooltips/applicationTooltips";

const application = useApplicationStore();
const quoteStore = useQuoteStore();
const v$ = useVuelidate();

const emit = defineEmits<{
    (on:"close"): void
}>();

const freshPensionFund : PensionFund = {
    type: null,
    provider: null,
    address: {
        line1: "",
        line2: "",
        city: "",
        county: "",
        postcode: "",
        country: "",
    },
    pensionSchemeName: "",
    planNumber: "",
    totalFund: 0,
    fullValue: null,
    fullValueReason: "",
    maxTaxFreeCash: null,
    taxFreeCashAmount: 0,
    pensionSharingOrder: null,
    bankruptcyOrder: null,
    earmarkingAttachmentOrder: null,
    otherCourtOrders: null,
};

const state = reactive({
    pensionFund: application.pensionFundsIndex !== null
        ? { ...application.pensionFunds[application.pensionFundsIndex] }
        : { ...freshPensionFund },
});

const validator = pensionFundsValidator(state.pensionFund);

state.pensionFund.maxTaxFreeCash = quoteStore.quote && quoteStore.quote.product.taxFree.type === TaxFreeType.Full;
state.pensionFund.taxFreeCashAmount = (quoteStore.quote && quoteStore.quote.product.taxFree.value) as number;

if (application.pensionFunds.length > 0) {
    const taxFreeFundsSum = computed(
        () => application.pensionFunds.reduce((acc, item) => acc + item.taxFreeCashAmount, 0),
    );

    if (application.pensionFundsIndex === null) {
        if (taxFreeFundsSum.value >= state.pensionFund.taxFreeCashAmount) {
            state.pensionFund.taxFreeCashAmount = 0;
        } else {
            state.pensionFund.taxFreeCashAmount -= taxFreeFundsSum.value;
        }
    } else {
        state.pensionFund.taxFreeCashAmount = application.pensionFunds[application.pensionFundsIndex].taxFreeCashAmount;
    }
}

const addToFund = async () => {
    state.pensionFund.taxFreeCashAmount = getTaxFreeCash(state.pensionFund);

    const isValid = await v$.value.$validate();

    if (isValid) {
        if (application.pensionFundsIndex !== null) {
            application.pensionFunds[application.pensionFundsIndex] = state.pensionFund;
        } else {
            application.pensionFunds.push(state.pensionFund);
        }
        emit("close");
    } else {
        scrollErrorIntoView();
    }
};

const pensionTypes = computed(() =>
    Object.values(PensionType).map(pensionType => ({ label: pensionType, value: pensionType })));

const pensionSharingOrder = {
    name: "pensionSharingOrder",
    label: "Pension Sharing Order",
    errorLabel: "pension sharing order",
    failState: true,
    extraInfoText: `Please send a copy of the Pension Sharing Order to ${application.companyName}
       to speed up the processing of your application.`,
    value: state.pensionFund.pensionSharingOrder,
};

const bankruptcyOrder : DefenceQuestion =
    {
        name: "bankruptcyOrder",
        label: "Bankruptcy Order",
        errorLabel: "bankruptcy order",
        failState: true,
        extraInfoText: `Please send a copy of the Bankruptcy Order to ${application.companyName}
          to speed up the processing of your application.`,
        value: state.pensionFund.bankruptcyOrder,
    };

const earmarkingAttachmentOrder : DefenceQuestion =
    {
        name: "earmarkingAttachmentOrder",
        label: "Earmarking/Attachment Order",
        errorLabel: "earmarking attachment order",
        failState: true,
        extraInfoText: `Please send a copy of the Earmarking or Attachment Orders to ${application.companyName}
         to speed up the processing of your application.`,
        value: state.pensionFund.earmarkingAttachmentOrder,
    };

const otherCourtOrders : DefenceQuestion =
    {
        name: "otherCourtOrders",
        label: "Other Court Orders",
        errorLabel: "other court orders",
        failState: true,
        extraInfoText: `Please send a copy of any court orders to ${application.companyName}
          to speed up the processing of your application.`,
        value: state.pensionFund.otherCourtOrders,
    };

</script>

<template>
    <Question
        name="type"
        label="What type of pension fund will you be using to purchase your annuity?"
        :tooltip="tooltips.typePensionFund"
    >
        <Select
            v-model="state.pensionFund.type"
            placeholder="Please select"
            :options="pensionTypes"
            :validation="validator.pensionType"
        />
    </Question>
    <Question
        name="provider"
        label="What is the name of your current provider?"
        :tooltip="tooltips.currentProvider"
    >
        <Input
            v-model="state.pensionFund.provider"
            placeholder="Provider Name"
            type="text"
            :validation="validator.provider"
        />
    </Question>

    <AddressSuggestion
        :address="state.pensionFund.address"
        :question-row="false"
        find-address-label="Find The Address"
        @address-update="(value: Address) => state.pensionFund.address = value"
    />
    <Question
        name="schemeName"
        label="What is the name of your pension scheme?"
        :tooltip="tooltips.schemeName"
    >
        <Input
            v-model="state.pensionFund.pensionSchemeName"
            placeholder="Scheme Name"
            type="text"
            :validation="validator.pensionSchemeName"
        />
    </Question>
    <Question
        name="planNumber"
        label="What is your existing pension plan number?"
    >
        <Input
            v-model="state.pensionFund.planNumber"
            placeholder="Plan Number"
            type="text"
            :validation="validator.existingPlanNumber"
        />
    </Question>
    <Question
        name="totalFund"
        label="What is the total fund value to be used to purchase your annuity (including tax-free lump sum)?"
    >
        <Input
            v-model.number="state.pensionFund.totalFund"
            type="currency"
            inputmode="text"
            placeholder="0"
            :validation="validator.totalFund"
            :filter="isDecimal"
        />
    </Question>
    <Question
        name="fullValue"
        class="mt-4"
        label="Are you using the full value of this pension fund?"
    >
        <RadioSet>
            <Radio
                v-model="state.pensionFund.fullValue"
                title="Yes"
                :value="true"
                :validation="validator.fullValue"
            />
            <Radio
                v-model="state.pensionFund.fullValue"
                title="No"
                :value="false"
                :validation="validator.fullValue"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="state.pensionFund.fullValue === false"
        name="fullValueReason"
        label="Why are you not using the full fund value?"
    >
        <TextArea
            v-model="state.pensionFund.fullValueReason"
            placeholder="Details Of Why"
            :validation="validator.fullValueReason"
        />
    </Question>
    <Question
        name="maxTaxFreeCash"
        label="Do you want to take the maximum amount of tax-free lump sum (normally 25% of the fund value)?"
    >
        <RadioSet>
            <Radio
                v-model="state.pensionFund.maxTaxFreeCash"
                title="Yes"
                :value="true"
                :disabled="true"
                :validation="validator.maxTaxFreeCash"
            />
            <Radio
                v-model="state.pensionFund.maxTaxFreeCash"
                title="No"
                :value="false"
                :disabled="true"
                :validation="validator.maxTaxFreeCash"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="state.pensionFund.maxTaxFreeCash === false"
        name="taxFreeCashAmount"
        label="How much tax-free lump sum would you like to take?"
    >
        <Input
            v-model.number="state.pensionFund.taxFreeCashAmount"
            type="currency"
            inputmode="text"
            placeholder="0"
            :validation="validator.taxFreeCashAmount"
            :filter="isDecimal"
        />
    </Question>
    <hr>
    <p class="font-weight-bold-col mb-3">
        Are your funds subject to any existing or proposed:
    </p>
    <DefenceLineQuestion
        :question="pensionSharingOrder"
        :question-row="false"
        @value-change="(value) => state.pensionFund.pensionSharingOrder = value"
    />
    <DefenceLineQuestion
        :question="bankruptcyOrder"
        :question-row="false"
        @value-change="(value) => state.pensionFund.bankruptcyOrder = value"
    />
    <DefenceLineQuestion
        :question="earmarkingAttachmentOrder"
        :question-row="false"
        @value-change="(value) => state.pensionFund.earmarkingAttachmentOrder = value"
    />
    <DefenceLineQuestion
        :question="otherCourtOrders"
        :question-row="false"
        @value-change="(value) => state.pensionFund.otherCourtOrders = value"
    />
    <hr>
    <div class="d-flex justify-content-end">
        <button
            type="button"
            class="btn btn-alert"
            @click.prevent="emit('close')"
        >
            Cancel
        </button>
        <button
            type="button"
            data-testid="addToFund"
            class="btn btn-primary ms-2"
            @click.prevent="addToFund()"
        >
            Save
        </button>
    </div>
</template>
